import axios from 'axios'
import * as P from './Protocol'
import { auth } from '../store/auth'

const BASE_URL = process.env.REACT_APP_API_URL ?? ''
const ProcUrl  = `${BASE_URL}/Main/Proc`

export interface ApiResult {
  resultCode:    string
  resultMessage: string
  resultData:    string
  rowCount:      number
  dataType:      number
  columnName:    string
  columnType:    string
}

async function httpPost(proc: string, requestData: string, token = ''): Promise<ApiResult> {
  const body = {
    token,
    sendTime:   new Date().toISOString().replace('T', ' ').slice(0, 19),
    proc,
    requestData,
    paramCount: requestData ? requestData.split(P.DELIM).length : 0,
  }
  const res = await axios.post<ApiResult>(ProcUrl, body)
  const data = res.data
  if (
    token &&
    (data.resultCode === P.RET_FORCE_LOGOUT || data.resultMessage === '세션이 만료되었습니다.')
  ) {
    auth.clear()
    window.dispatchEvent(new CustomEvent('session-expired'))
  }
  return data
}

// ── 사용자 ────────────────────────────────────────────────
export const apiUserDupCheck = (userId: string) =>
  httpPost(P.PID_USER_DUP_CHECK, userId)

export const apiUserRegister = (userId: string, userPass: string, nickname: string) =>
  httpPost(P.PID_USER_REGISTER, [userId, userPass, nickname].join(P.DELIM))

export const apiUserLogin = (userId: string, userPass: string) =>
  httpPost(P.PID_USER_LOGIN, [userId, userPass].join(P.DELIM))

export const apiUserLogout = (token: string) =>
  httpPost(P.PID_USER_LOGOUT, '', token)

// ── 내정보 변경 (SEQ=10110 STEP=4) ────────────────────────
export const apiUserUpdateNickname = (token: string, newNickname: string) =>
  httpPost(P.PID_USER_UPDATE_NICKNAME, newNickname, token)

export const apiUserChangePassword = (token: string, currentPass: string, newPass: string) =>
  httpPost(P.PID_USER_CHANGE_PASSWORD, [currentPass, newPass].join(P.DELIM), token)

// ── 룸 ───────────────────────────────────────────────────
export const apiRoomList = (
  token: string,
  gameType: string,
  page: number,
  pageSize: number,
) => httpPost(P.PID_ROOM_LIST, [gameType, page, pageSize].join(P.DELIM), token)

export const apiRoomDetail = (token: string, roomCode: string) =>
  httpPost(P.PID_ROOM_DETAIL, roomCode, token)

// ── 카탄 ─────────────────────────────────────────────────
// PID 20101: 보드 + 게임 상태 통합 조회
// roomCode 필수, matchId 0 이거나 빈 문자열이면 서버가 룸코드로 현재 매치 자동 조회
export async function apiCatanGameState(
  token: string,
  roomCode: string,
  matchId: number = 0,
): Promise<{
  matchId: number; roomCode: string; seed: number; status: string
  currentTurnUserCode: number | null; currentPhase: string | null
  players: object[]; hexes: object[]; vertices: object[]; edges: object[]; ports: object[]
} | null> {
  const reqData = matchId > 0
    ? [roomCode, String(matchId)].join(P.DELIM)
    : roomCode
  const res = await httpPost(P.PID_CATAN_GAME_STATE, reqData, token)
  if (res.resultCode !== P.RET_OK) return null
  try {
    return JSON.parse(res.resultData)
  } catch {
    return null
  }
}

// ── 티켓 투 라이드 (SEQ=10147 운영 가동) ─────────────────
// PID 21101: TTR 보드 + 게임 상태 통합 조회 (재연결/진입)
// roomCode 필수, matchId 0 이면 서버가 룸코드로 활성 matchId 자동 조회.
// 결과는 sp_TtrGameLoad 10 결과셋 JSON wrapper 패스스루
//   ({ matchHeader, players, faceUpCards, routes, myHand, myTickets,
//      cardCounts, ticketCounts, trainRemain, ticketDeckRemain }) —
//   ticketStore.applyGameState 가 매핑.
export async function apiTtrGameState(
  token: string,
  roomCode: string,
  matchId: number = 0,
): Promise<Record<string, unknown> | null> {
  const reqData = matchId > 0
    ? [roomCode, String(matchId)].join(P.DELIM)
    : roomCode
  const res = await httpPost(P.PID_TTR_GAME_STATE, reqData, token)
  if (res.resultCode !== P.RET_OK) return null
  try {
    return JSON.parse(res.resultData)
  } catch {
    return null
  }
}

// ── 루미큐브 (SEQ=10220 신설) ────────────────────────────
// PID 22001: 루미큐브 게임 상태 통합 조회 (sp_RummikubGameSnapshot 7 resultset)
// roomCode 필수, matchId 0 이면 서버가 룸코드로 활성 matchId 자동 조회.
// 결과는 sp_RummikubGameSnapshot 7 결과셋 JSON wrapper 패스스루
//   ({ matchHeader, players, boardSets, boardTiles, myHand, otherHandSizes, roundScoresIfAny }) —
//   rummikubStore.applyGameState 가 매핑.
export async function apiRummikubGameState(
  token: string,
  roomCode: string,
  matchId: number = 0,
): Promise<Record<string, unknown> | null> {
  const reqData = matchId > 0
    ? [roomCode, String(matchId)].join(P.DELIM)
    : roomCode
  const res = await httpPost(P.PID_RUMK_GAME_STATE, reqData, token)
  if (res.resultCode !== P.RET_OK) return null
  try {
    return JSON.parse(res.resultData)
  } catch {
    return null
  }
}

// ── 친구 (P-Track 0) ─────────────────────────────────────

export interface FriendInfo {
  userCode:    number
  nickname:    string
  lastLoginAt: string | null
  acceptedAt:  string | null
}

export interface FriendRequestInfo {
  fromUserCode: number
  fromNickname: string
  requestedAt:  string
}

export interface FriendSentInfo {
  toUserCode:  number
  toNickname:  string
  requestedAt: string
}

export interface FriendListData {
  friends:  FriendInfo[]
  received: FriendRequestInfo[]
  sent:     FriendSentInfo[]
}

export interface FriendSearchUser {
  userCode:       number
  nickname:       string
  relationStatus: 'NONE' | 'PENDING' | 'ACCEPTED' | 'BLOCKED'
  isMyRequest:    number
}

export async function apiFriendList(token: string): Promise<FriendListData | null> {
  const res = await httpPost(P.PID_FRIEND_LIST, '', token)
  if (res.resultCode !== P.RET_OK) return null
  try { return JSON.parse(res.resultData) as FriendListData }
  catch { return null }
}

export const apiFriendRequest = (token: string, targetUserCode: number) =>
  httpPost(P.PID_FRIEND_REQUEST, String(targetUserCode), token)

export const apiFriendRespond = (token: string, requesterCode: number, action: 'ACCEPT' | 'REJECT') =>
  httpPost(P.PID_FRIEND_RESPOND, [String(requesterCode), action].join(P.DELIM), token)

export const apiFriendDelete = (token: string, targetUserCode: number) =>
  httpPost(P.PID_FRIEND_DELETE, String(targetUserCode), token)

export const apiFriendBlock = (token: string, targetUserCode: number, action: 'BLOCK' | 'UNBLOCK') =>
  httpPost(P.PID_FRIEND_BLOCK, [String(targetUserCode), action].join(P.DELIM), token)

export async function apiFriendSearch(token: string, keyword: string): Promise<FriendSearchUser[]> {
  const res = await httpPost(P.PID_FRIEND_SEARCH, keyword, token)
  if (res.resultCode !== P.RET_OK) return []
  try { return JSON.parse(res.resultData) as FriendSearchUser[] }
  catch { return [] }
}

// ── Phase 1.5: 자동 복귀용 내 룸 조회 ──────────────────────
// 응답 resultData 파싱: room=null → 룸 없음, room={...} → 복귀 대상
export async function apiRoomMy(token: string): Promise<{ room: object | null; members: object[] } | null> {
  const res = await httpPost(P.PID_ROOM_MY, '', token)
  if (res.resultCode !== P.RET_OK) return null
  try {
    const parsed = JSON.parse(res.resultData)
    return { room: parsed.room ?? null, members: parsed.members ?? [] }
  } catch {
    return null
  }
}
